import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Book, Clock, Globe, Brain, MessageSquare, ArrowRight } from 'lucide-react';
import { AuthModal } from './components/AuthModal';
import { useAuth } from './contexts/AuthContext';

function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isAuthModalOpen, setAuthModalOpen] = useState(false);

  const handleProtectedNavigation = (route) => {
    if (user) {
      navigate(route);
    } else {
      setAuthModalOpen(true);
    }
  };

  const closeAuthModal = () => setAuthModalOpen(false);

  return (
    <div className="min-h-screen bg-parchment font-serif">
      {/* Hero Section */}
      <header className="relative overflow-hidden pt-16">
        <div className="max-w-7xl mx-auto">
          <div className="relative z-10 pb-8 sm:pb-16 md:pb-20 lg:max-w-2xl lg:w-full lg:pb-28 xl:pb-32">
            <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
              <div className="sm:text-center lg:text-left">
                <h1 className="vintage-title">
                  <span className="block">Journey Through</span>
                  <span className="block text-parchment-dark">History's Pages</span>
                </h1>
                <p className="vintage-subtitle mt-3 sm:mt-5 sm:max-w-xl sm:mx-auto md:mt-5 lg:mx-0">
                  Embark on a captivating journey through time with our AI-powered historical exploration platform. Discover stories, events, and knowledge that shaped our world.
                </p>
                <div className="mt-5 sm:mt-8 sm:flex sm:justify-center lg:justify-start">
                  <button
                    onClick={() => handleProtectedNavigation("/chat")}
                    className="vintage-button flex items-center gap-2 text-lg px-8 py-4"
                  >
                    Begin Your Journey
                    <ArrowRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </main>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-1/2 h-full bg-parchment-light rounded-l-full opacity-50 transform translate-x-1/3"></div>
      </header>

      {/* Features Section */}
      <section className="py-16 bg-parchment-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-parchment-dark sm:text-4xl">
              Explore History's Treasures
            </h2>
            <p className="mt-4 max-w-2xl mx-auto text-xl text-parchment-dark/80 italic">
              Discover the past through our comprehensive collection of historical resources and interactive learning tools.
            </p>
          </div>

          <div className="mt-16">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
              <div 
                className="group cursor-pointer transform transition-all duration-300 hover:-translate-y-2"
                onClick={() => handleProtectedNavigation("/chat")}
              >
                <div className="vintage-card h-full">
                  <div className="flex flex-col items-center">
                    <div className="p-3 bg-parchment-dark text-parchment-light rounded-lg shadow-vintage">
                      <MessageSquare className="h-8 w-8" />
                    </div>
                    <h3 className="mt-8 text-xl font-bold text-parchment-dark">Historical Dialogue</h3>
                    <p className="mt-4 text-center text-parchment-dark/80">
                      Engage in enlightening conversations with our AI tutor about any historical topic
                    </p>
                  </div>
                </div>
              </div>

              <div 
                className="group cursor-pointer transform transition-all duration-300 hover:-translate-y-2"
                onClick={() => navigate("/timeline")}
              >
                <div className="vintage-card h-full">
                  <div className="flex flex-col items-center">
                    <div className="p-3 bg-parchment-dark text-parchment-light rounded-lg shadow-vintage">
                      <Clock className="h-8 w-8" />
                    </div>
                    <h3 className="mt-8 text-xl font-bold text-parchment-dark">Historical Timeline</h3>
                    <p className="mt-4 text-center text-parchment-dark/80">
                      Journey through time with our interactive historical timeline explorer
                    </p>
                  </div>
                </div>
              </div>

              <div 
                className="group cursor-pointer transform transition-all duration-300 hover:-translate-y-2"
                onClick={() => navigate("/map")}
              >
                <div className="vintage-card h-full">
                  <div className="flex flex-col items-center">
                    <div className="p-3 bg-parchment-dark text-parchment-light rounded-lg shadow-vintage">
                      <Globe className="h-8 w-8" />
                    </div>
                    <h3 className="mt-8 text-xl font-bold text-parchment-dark">World History</h3>
                    <p className="mt-4 text-center text-parchment-dark/80">
                      Explore historical events across the globe with our interactive map
                    </p>
                  </div>
                </div>
              </div>

              <div 
                className="group cursor-pointer transform transition-all duration-300 hover:-translate-y-2"
                onClick={() => handleProtectedNavigation("/quiz")}
              >
                <div className="vintage-card h-full">
                  <div className="flex flex-col items-center">
                    <div className="p-3 bg-parchment-dark text-parchment-light rounded-lg shadow-vintage">
                      <Brain className="h-8 w-8" />
                    </div>
                    <h3 className="mt-8 text-xl font-bold text-parchment-dark">Knowledge Quest</h3>
                    <p className="mt-4 text-center text-parchment-dark/80">
                      Test your historical knowledge with our adaptive quiz system
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="bg-parchment-dark">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <div>
            <h2 className="text-3xl font-extrabold tracking-tight text-parchment-light sm:text-4xl">
              <span className="block">Ready to Begin Your Historical Journey?</span>
              <span className="block text-parchment/80 mt-2 text-2xl italic">
                Start exploring the fascinating stories of our past.
              </span>
            </h2>
          </div>
          <div className="mt-8 lg:mt-0 lg:flex-shrink-0">
            <button
              onClick={() => handleProtectedNavigation("/chat")}
              className="inline-flex items-center px-8 py-4 border-2 border-parchment-light text-xl font-medium rounded-lg text-parchment-light hover:bg-parchment-light hover:text-parchment-dark transition-colors duration-200"
            >
              Start Exploring
              <ArrowRight className="ml-2 h-6 w-6" />
            </button>
          </div>
        </div>
      </section>

      {/* Auth Modal */}
      <AuthModal isOpen={isAuthModalOpen} onClose={closeAuthModal} />
    </div>
  );
}

export default LandingPage;